import React from 'react';
import { Ticket, LogIn, Clock, Info, CreditCard, Mail, AlertCircle, Users, FileText, CheckCircle, XCircle } from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const TicketPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const reservationProcedures = [
    {
      icon: LogIn,
      number: '1.1',
      title: 'Access to Booking Services',
      content: `To access booking services, users must log in to the ${COMPANY_CONFIG.appname} website or mobile application using valid user credentials.`
    },
    {
      icon: Clock,
      number: '1.2',
      title: 'Booking Timing',
      content: 'Booking requests should be made at least 3 hours before the intended departure time.'
    },
    {
      icon: Info,
      number: '1.3',
      title: 'Information Display',
      content: `Information related to available vehicles, travel dates, departure and arrival times, seats, boarding points, and ticket prices will be displayed on the ${COMPANY_CONFIG.appname} platform.`
    },
    {
      icon: Ticket,
      number: '1.4',
      title: 'Selection Process',
      content: 'After reviewing the available options, users may select their preferred vehicle, seat, travel date and time, start point, and destination.'
    },
    {
      icon: CreditCard,
      number: '1.5',
      title: 'Payment Requirements',
      content: `Full payment must be completed at the time of booking. The total amount includes ticket fare, applicable taxes, and any platform, service, or convenience fees charged by ${COMPANY_CONFIG.appname}.`
    },
    {
      icon: Mail,
      number: '1.6',
      title: 'E-Ticket Issuance',
      content: `Once the booking is confirmed, an e-ticket will be emailed to the customer. All e-tickets issued by ${COMPANY_CONFIG.appname} are non-transferable.`
    },
    {
      icon: AlertCircle,
      number: '1.7',
      title: 'Approximate Times',
      content: 'The departure and arrival times shown on the ticket are approximate and subject to change based on operational conditions.'
    },
    {
      icon: FileText,
      number: '1.8',
      title: 'Contractual Agreement',
      content: `By making a reservation through the ${COMPANY_CONFIG.appname} platform, you enter into a direct contractual agreement with the respective third-party transport service provider.`
    },
    {
      icon: Users,
      number: '1.9',
      title: 'Provider Contact',
      content: 'Passengers are strongly advised to contact the transport provider directly for confirmation of boarding points or any trip-related inquiries.'
    },
    {
      icon: CheckCircle,
      number: '1.10',
      title: 'Boarding Requirements',
      content: 'At the time of boarding, passengers must present: (a) A printed or digital copy of the e-ticket; and (b) A valid identity document (NIC, Driving License, Passport, Company ID, Student ID, etc.).'
    },
    {
      icon: XCircle,
      number: '1.11',
      title: 'Denial of Boarding',
      content: 'Failure to provide a valid ticket and ID may result in denial of boarding.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-1">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">Ticket Policies</h1>
            <p className="text-xs sm:text-sm text-blue-100">{COMPANY_CONFIG.appname}</p>
            <p className="text-xs text-blue-200 mt-1">Last Updated: {currentDate}</p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed text-lg">
              Please read these ticket policies carefully before making a reservation. By booking through 
              {COMPANY_CONFIG.appname}, you agree to comply with these policies and procedures.
            </p>
          </div>
        </div>
      </section>

      {/* Reservation Procedures Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                1. Reservation Procedures
              </h2>
            </div>
          </div>

          <div className="space-y-6">
            {reservationProcedures.map((procedure, index) => {
              const Icon = procedure.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="text-blue-600 font-bold text-lg">
                          {procedure.number}
                        </span>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {procedure.title}
                        </h3>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {procedure.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Notes Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Important Reminders
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    <span>Always carry a valid ID document matching the name on your ticket</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    <span>Arrive at the boarding point at least 15 minutes before departure</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    <span>Keep your e-ticket accessible (printed or digital) for verification</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    <span>Contact the transport provider directly for any trip-related inquiries</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Need More Information?
            </h3>
            <p className="text-gray-600 mb-4">
              If you have questions about our ticket policies or need assistance with your booking, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Contact Us
              </a>
              <a
                href="/terms"
                className="inline-block bg-white text-blue-600 border-2 border-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                View Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TicketPolicy;

